import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import Dialog from "@components/Dialog";
import Input from "@components/Input";
import SizedBox from "@components/SizedBox";
import Text from "@components/Text";
import Checkbox from "@components/Checkbox";
import { Row, Column } from "@components/Flex";
import Button from "@components/Button";
import { observer } from "mobx-react-lite";
import { useLang, useLength, useSettings } from "@src/hooks/useLang";
import { LengthUnit } from "@services/demosService";
import {
  convertLength,
  roundLengthForDisplay,
  roundLengthForStorage,
} from "@src/utils/convert";
import { IDialogPropTypes } from "rc-dialog/es/IDialogPropTypes";
import {
  TDevice,
  TWidthSegment,
  getDeviceWidthSegments,
  setDeviceWidthSegments,
} from "@services/devicesService";
import WidthHistoryEditor, {
  restatesHistory,
  toLocalInput,
  type WidthRow,
} from "@components/WidthHistoryEditor";
import Loading from "@components/Loading";
import { useDevicesScreenVM } from "@screens/DevicesScreen/DevicesScreenVM";
import { planWidthSave } from "./deviceWidthSave";
import usePrevious from "@src/hooks/usePrevious";
import { useStores } from "@stores";

const AddBtn = styled.button`
  background: none;
  border: 1px dashed #cbd5e1;
  border-radius: 6px;
  color: #494c5b;
  cursor: pointer;
  font-size: 13px;
  padding: 8px;
  width: 100%;
`;

const AddBlock = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 10px 12px;
`;

const Field = styled.input`
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 13px;
  padding: 6px 8px;
  width: 100%;
`;

const LinkBtn = styled.button`
  background: none;
  border: none;
  color: #3b82f6;
  cursor: pointer;
  font-size: 13px;
  padding: 0;
`;

/** devices.delay column default, in ms. */
const DEFAULT_DELAY_MS = 16000;
/** Mirrors the backend bound (updateDeviceSchema.delay, 120 000 ms). */
const MAX_DELAY_SECONDS = 120;
/** Mirrors backend MAX_WIDTH_METERS (lib/widthSegments.ts): no header is wider,
 *  and a 67 typed for 6.7 would divide every yield figure by ten. */
const MAX_WIDTH_METERS = 30;

interface IProps extends IDialogPropTypes {
  /** `sendWidth: false` when the width field was left as it opened (planWidthSave). */
  onSubmit?: (params: TDevice, options?: { sendWidth: boolean }) => Promise<void>;
  onClose?: () => void;
}

const CreateDeviceDialog: React.FC<IProps> = ({
  onSubmit,
  onClose,
  ...rest
}) => {
  const lang = useLang();
  const t = (lang.devices ?? {}) as any;
  const vm = useDevicesScreenVM();
  const { accountStore, farmStore } = useStores();
  const locLength = useLength();
  const { length: lengthUnit } = useSettings();
  const [initialized, setInitialized] = useState(false);
  const [serial, setSerial] = useState<string | undefined>();
  const [name, setName] = useState<string | undefined>();
  const [model, setModel] = useState<string | undefined>();
  // Width is shown and typed in the user's length unit; the API always takes
  // metres. `prefilledWidth` lets an untouched field submit the stored metres
  // value verbatim instead of a m -> ft -> m round-trip (6.7 -> 21.98 -> 6.6995).
  const [width, setWidth] = useState<string | number | undefined>();
  const prefilledWidth = useRef<{ display: number; meters: number } | null>(null);
  // The width the dialog opened with, in metres: the newest timeline segment
  // once loaded, else the device's own width. A save sends width only when the
  // field differs from it. `widthTyped` keeps a late segment load from
  // overwriting what the user already typed.
  const baselineWidth = useRef<number | null>(null);
  const widthTyped = useRef(false);
  const [latestSegmentMeters, setLatestSegmentMeters] = useState<number | null>(null);
  // Header-width history — edit mode only. `loadedWidthRows` is the list the
  // GET returned, kept so we can tell an appended change (harmless) from an
  // edit of an existing line (restates past days) without asking the server.
  const [widthRows, setWidthRows] = useState<WidthRow[] | null>(null);
  const loadedWidthRows = useRef<WidthRow[]>([]);
  const [showWidthHistory, setShowWidthHistory] = useState(false);
  // "A different header was fitted" — the only action that opens a new dated
  // segment. Kept separate from the width field above, which corrects the
  // current one in place.
  const [addingNewWidth, setAddingNewWidth] = useState(false);
  const [newWidthWhen, setNewWidthWhen] = useState("");
  const [newWidthValue, setNewWidthValue] = useState("");
  const [newWidthError, setNewWidthError] = useState<string | null>(null);
  // Grain-flow delay — edit mode only. Typed in seconds, stored in ms on
  // devices.delay (the admin dialog edits the same column in ms). An untouched
  // field resubmits nothing, so a stored 15500 ms never round-trips to 16000.
  const [delaySeconds, setDelaySeconds] = useState<string>("");
  const prefilledDelay = useRef<{ display: string; ms: number } | null>(null);
  // Farm visibility — owner-only, edit mode only.
  const [allFarms, setAllFarms] = useState(true);
  const [farmIds, setFarmIds] = useState<string[]>([]);
  const disabled = [serial, name, model, width].some((v) => !v);
  const prevVisible = usePrevious(rest.visible);

  const showFarms = vm.editedDevice != null && !accountStore.isMember;

  const reset = () => {
    setSerial(undefined);
    setName(undefined);
    setModel(undefined);
    setWidth(undefined);
    prefilledWidth.current = null;
    baselineWidth.current = null;
    widthTyped.current = false;
    setLatestSegmentMeters(null);
    setDelaySeconds("");
    prefilledDelay.current = null;
    setWidthRows(null);
    loadedWidthRows.current = [];
    setShowWidthHistory(false);
    setAddingNewWidth(false);
    setNewWidthWhen("");
    setNewWidthValue("");
    setNewWidthError(null);
    setAllFarms(true);
    setFarmIds([]);
  };

  // The width to send to the API, always in metres.
  const widthInMeters = (): number => {
    const entered = Number(width);
    const prefilled = prefilledWidth.current;
    if (prefilled != null && entered === prefilled.display) {
      return prefilled.meters;
    }
    return roundLengthForStorage(
      convertLength(entered, lengthUnit, LengthUnit.Meter)
    );
  };

  /** "Header width must be 30 m or less", in the user's length unit. */
  const widthTooLargeMessage = (): string => {
    const max = `${roundLengthForDisplay(
      convertLength(MAX_WIDTH_METERS, LengthUnit.Meter, lengthUnit)
    )} ${locLength()}`;
    return (t.widthErrorTooLarge ?? "Header width must be {max} or less").replace("{max}", max);
  };

  const toggleFarm = (id: string) =>
    setFarmIds((prev) =>
      prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]
    );

  /** Show `meters` in the field above and remember the exact stored value. */
  const showWidthMeters = (meters: number) => {
    const display = roundLengthForDisplay(
      convertLength(meters, LengthUnit.Meter, lengthUnit)
    );
    prefilledWidth.current = { display, meters };
    setWidth(display);
  };

  const openAddNewWidth = () => {
    const now = new Date();
    setNewWidthWhen(toLocalInput(now.toISOString()));
    setNewWidthValue(String(width ?? ""));
    setNewWidthError(null);
    setAddingNewWidth(true);
  };

  /**
   * Append a segment starting at the chosen instant. Everything before it keeps
   * the width it was harvested with; the field above moves to the new value
   * because that is now the current header.
   */
  const confirmAddNewWidth = () => {
    const rows = widthRows ?? [];
    const when = new Date(newWidthWhen);
    const entered = Number(newWidthValue);
    const last = rows[rows.length - 1];

    if (isNaN(when.getTime())) {
      setNewWidthError(t.widthErrorDate ?? "Pick a date");
      return;
    }
    if (when.getTime() > Date.now() + 60_000) {
      setNewWidthError(
        t.widthErrorFuture ?? "A header width cannot start in the future"
      );
      return;
    }
    if (last && when.getTime() <= new Date(last.effectiveFrom).getTime()) {
      // Backdating before the current segment is the history editor's job.
      setNewWidthError(
        t.widthErrorBeforeCurrent ??
          "This must start after the width currently in force"
      );
      return;
    }
    if (!(entered > 0)) {
      setNewWidthError(t.widthErrorValue ?? "Enter a width");
      return;
    }

    const meters = roundLengthForStorage(
      convertLength(entered, lengthUnit, LengthUnit.Meter)
    );
    if (meters > MAX_WIDTH_METERS) {
      setNewWidthError(widthTooLargeMessage());
      return;
    }
    setWidthRows([...rows, { effectiveFrom: when.toISOString(), widthMeters: meters }]);
    showWidthMeters(meters);
    setAddingNewWidth(false);
    setShowWidthHistory(true);
  };

  /** The delay to send in ms, or undefined to leave the stored value alone. */
  const delayToSubmit = (): number | undefined | null => {
    if (!vm.editedDevice) return undefined;
    const prefilled = prefilledDelay.current;
    if (prefilled && delaySeconds.trim() === prefilled.display) return undefined;
    const s = Number(delaySeconds.replace(",", "."));
    if (delaySeconds.trim() === "" || !Number.isFinite(s) || s < 0 || s > MAX_DELAY_SECONDS) {
      return null;
    }
    return Math.round(s * 1000);
  };

  const handleSubmit = async () => {
    if (!serial || !name || !model || !width) return;

    const delayMs = delayToSubmit();
    if (delayMs === null) {
      window.alert(
        t.dataDelayInvalid ?? `Data delay must be between 0 and ${MAX_DELAY_SECONDS} seconds`
      );
      return;
    }

    // The width field is the CURRENT width, so an edit writes into the newest
    // row rather than opening a new one. A new header is added through
    // `confirmAddNewWidth`, which has already put its dated row on the end.
    // Width is written only when it changed (planWidthSave).
    const meters = widthInMeters();
    const plan = planWidthSave({
      meters,
      baselineMeters: vm.editedDevice ? baselineWidth.current : null,
      rows: vm.editedDevice ? widthRows : null,
      loadedRows: loadedWidthRows.current,
    });
    if (plan.sendWidth && (!(meters > 0) || meters > MAX_WIDTH_METERS)) {
      window.alert(meters > MAX_WIDTH_METERS ? widthTooLargeMessage() : t.widthErrorValue ?? "Enter a width");
      return;
    }
    const rows = plan.rows;

    // The whole list goes in one PUT. Correcting the current width or adding a
    // dated change is routine; anything that restates a closed day is confirmed.
    if (vm.editedDevice && rows) {
      const loaded = loadedWidthRows.current;
      if (restatesHistory(loaded, rows)) {
        const ok = window.confirm(
          t.widthHistoryConfirm ??
            "This changes harvested area and yield for days already recorded. Continue?"
        );
        if (!ok) return;
      }
      try {
        await setDeviceWidthSegments(
          vm.editedDevice.id,
          rows.map((r) => ({
            // The earliest row governs all earlier time, so its own instant
            // is immaterial — keep whatever it already had.
            effectiveFrom: r.effectiveFrom,
            widthMeters: r.widthMeters,
          }))
        );
        loadedWidthRows.current = rows;
        setWidthRows(rows);
      } catch (e: any) {
        // The device save below would mirror a width the timeline rejected.
        console.error("width history save failed", e);
        window.alert(
          e?.message ?? t.widthHistoryError ?? "Failed to save the header width"
        );
        return;
      }
    }

    const params: TDevice = {
      id: serial,
      esn: serial,
      harvester: { name, model, default_reaper: { width: meters } },
      tags: [],
      status: "inactive",
      ...(delayMs !== undefined ? { delay: delayMs } : {}),
      ...(showFarms
        ? { farms: { allFarms, farmIds: allFarms ? [] : farmIds } }
        : {}),
    };
    onSubmit && onSubmit(params, { sendWidth: plan.sendWidth });
  };

  const handleClose = useCallback(() => {
    setInitialized(false);
    reset();
    onClose && onClose();
  }, [onClose]);

  useEffect(() => {
    if (!initialized && vm.editedDevice) {
      setSerial(vm.editedDevice.id);
      setName(vm.editedDevice.harvester.name);
      setModel(vm.editedDevice.harvester.model);
      const meters = vm.editedDevice.harvester.default_reaper.width;
      const display = roundLengthForDisplay(
        convertLength(meters, LengthUnit.Meter, lengthUnit)
      );
      prefilledWidth.current = { display, meters };
      setWidth(display);
      baselineWidth.current = meters;
      // 16 s is the column default; a device listed without a value has it.
      const delayMs = vm.editedDevice.delay ?? DEFAULT_DELAY_MS;
      const delayDisplay = String(Math.round(delayMs / 100) / 10);
      prefilledDelay.current = { display: delayDisplay, ms: delayMs };
      setDelaySeconds(delayDisplay);
      setAllFarms(vm.editedDevice.farms?.allFarms ?? true);
      setFarmIds(vm.editedDevice.farms?.farmIds ?? []);
      setInitialized(true);
    }
  }, [initialized, vm.editedDevice, lengthUnit]);

  // Load the header-width history for the device being edited. Deliberately
  // keyed on the device id alone: `lengthUnit` must NOT be a dependency here —
  // the rows are stored in metres and only converted at render time, and
  // useSettings() rebuilds a fresh object every render, so depending on
  // anything it returns would refetch forever.
  const editedDeviceId = vm.editedDevice?.id;
  useEffect(() => {
    let cancelled = false;
    if (!editedDeviceId) return;
    getDeviceWidthSegments(editedDeviceId)
      .then((segments: TWidthSegment[]) => {
        if (cancelled) return;
        const rows: WidthRow[] = segments.map((sg) => ({
          effectiveFrom: sg.effectiveFrom,
          widthMeters: sg.widthMeters,
        }));
        loadedWidthRows.current = rows;
        setWidthRows(rows);
        if (rows.length > 0) setLatestSegmentMeters(rows[rows.length - 1].widthMeters);
      })
      .catch(() => {
        // History is an enhancement: a device with none still edits its width
        // through the plain field above.
        if (!cancelled) setWidthRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [editedDeviceId]);

  // The list's width is devices.width (6.7 m for a John Deere combine before
  // its first import), not necessarily the newest segment: once the timeline
  // loads, the field and the baseline follow its newest row, unless the user
  // has already typed a width.
  useEffect(() => {
    if (latestSegmentMeters == null) return;
    baselineWidth.current = latestSegmentMeters;
    if (widthTyped.current) return;
    const display = roundLengthForDisplay(
      convertLength(latestSegmentMeters, LengthUnit.Meter, lengthUnit)
    );
    prefilledWidth.current = { display, meters: latestSegmentMeters };
    setWidth(display);
  }, [latestSegmentMeters, lengthUnit]);

  useEffect(() => {
    if (prevVisible && !rest.visible) handleClose();
  }, [handleClose, prevVisible, rest.visible]);

  return (
    <Dialog
      destroyOnClose
      style={{ width: 384 }}
      title={
        vm.editedDevice == null
          ? lang.devices.newDeviceTitle
          : lang.devices.editDeviceTitle
      }
      onClose={handleClose}
      {...rest}
    >
      {vm.editedDevice == null && (
        <>
          <Input
            value={serial}
            onChange={(e) => setSerial(e.target.value)}
            label={lang.devices.serialNumberTitle}
            placeholder={lang.devices.serialNumberSuggest}
            tip={lang.devices.serialNumberHelp}
          />
          <SizedBox height={24} />
        </>
      )}
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        label={lang.devices.harvesterNumberTitle}
        placeholder={lang.devices.harvesterNumberSuggest}
        tip={lang.devices.harvesterNumberHelp}
      />
      <SizedBox height={24} />
      <Input
        value={model}
        onChange={(e) => setModel(e.target.value)}
        label={lang.devices.harvesterModelTitle}
        placeholder={lang.devices.harvesterModelSuggestion}
      />
      <SizedBox height={24} />
      <Input
        value={width}
        onChange={(e) => {
          widthTyped.current = true;
          setWidth(e.target.value);
        }}
        label={`${lang.devices.headerWidthTitle} (${locLength()})`}
        placeholder={"0"}
        type={"number"}
      />
      {vm.editedDevice != null && widthRows != null && (
        <>
          <SizedBox height={8} />
          <Text size={12} color="#899093">
            {t.widthHistoryHint ??
              "This is the width on the machine now. Correcting it also corrects the area and yield already recorded with it."}
          </Text>
          <SizedBox height={12} />
          {!addingNewWidth ? (
            <AddBtn type="button" onClick={openAddNewWidth}>
              {t.widthAddNew ?? "+ Add new header width"}
            </AddBtn>
          ) : (
            <AddBlock>
              <Text size={12} color="#899093">
                {t.widthAddNewHint ??
                  "Use this when a different header was fitted. Days before the date keep the old width."}
              </Text>
              <SizedBox height={8} />
              <Text size={12} color="#899093">
                {t.widthFittedOn ?? "Fitted on"}
              </Text>
              <SizedBox height={4} />
              <Field
                type="datetime-local"
                value={newWidthWhen}
                max={toLocalInput(new Date().toISOString())}
                onChange={(e) => {
                  setNewWidthWhen(e.target.value);
                  setNewWidthError(null);
                }}
              />
              <SizedBox height={8} />
              <Text size={12} color="#899093">
                {`${lang.devices.headerWidthTitle} (${locLength()})`}
              </Text>
              <SizedBox height={4} />
              <Field
                type="number"
                value={newWidthValue}
                onChange={(e) => {
                  setNewWidthValue(e.target.value);
                  setNewWidthError(null);
                }}
              />
              {newWidthError != null && (
                <>
                  <SizedBox height={6} />
                  <Text size={12} color="#d14343">
                    {newWidthError}
                  </Text>
                </>
              )}
              <SizedBox height={10} />
              <Row>
                <Button kind="outline" onClick={() => setAddingNewWidth(false)}>
                  {lang.devices.deviceCancelButton}
                </Button>
                <SizedBox width={12} />
                <Button onClick={confirmAddNewWidth}>
                  {t.widthAddConfirm ?? "Add"}
                </Button>
              </Row>
            </AddBlock>
          )}
          <SizedBox height={8} />
          <LinkBtn
            type="button"
            onClick={() => setShowWidthHistory((v) => !v)}
          >
            {showWidthHistory
              ? t.widthHistoryHide ?? "Hide width history"
              : `${t.widthHistoryShow ?? "Width history"} (${widthRows.length})`}
          </LinkBtn>
          {showWidthHistory && (
            <>
              <SizedBox height={8} />
              <WidthHistoryEditor
                rows={widthRows}
                // The field above and the newest row are the same number, so an
                // edit to the last line has to move the field with it —
                // otherwise submitting would write the field's stale value back.
                onChange={(next) => {
                  setWidthRows(next);
                  const last = next[next.length - 1];
                  if (last) showWidthMeters(last.widthMeters);
                }}
                lengthUnit={lengthUnit}
                unitLabel={locLength()}
                labels={{
                  fromTheStart: t.widthFromStart ?? "From the start",
                  changedOn: t.widthChangedOn ?? "Changed on",
                  addEarlier: t.widthAddEarlier ?? "+ Add earlier width",
                  remove: t.widthRemove ?? "Remove",
                  restatesWarning:
                    t.widthHistoryWarning ??
                    "Changing or removing a line here recalculates area and yield for the days it covers.",
                }}
              />
            </>
          )}
        </>
      )}
      {/* Not for John Deere imports: Deere logs points already aligned (delay 0). */}
      {vm.editedDevice != null && vm.editedDevice.source !== "johndeere" && (
        <>
          <SizedBox height={24} />
          <Input
            value={delaySeconds}
            onChange={(e) => setDelaySeconds(e.target.value)}
            label={`${t.dataDelayTitle ?? "Data delay"} (${t.dataDelayUnit ?? "s"})`}
            placeholder={"16"}
            type={"number"}
            tip={
              t.dataDelayTooltip ??
              "Time from cutting until the crop reaches the yield sensor. Readings are shifted back along the track by this much."
            }
          />
        </>
      )}
      <SizedBox height={24} />
      {showFarms && (
        <>
          <Text size={14} type="heading" color="#494C5B">
            {t.deviceFarmsTitle ?? "Visible to farms"}
          </Text>
          <SizedBox height={4} />
          <Row
            alignItems="center"
            justifyContent="space-between"
            style={{ padding: "6px 0" }}
          >
            <Text size={14} color="#494C5B">
              {t.deviceAllFarms ?? "All farms (including future ones)"}
            </Text>
            <Checkbox checked={allFarms} onChange={setAllFarms} />
          </Row>
          {!allFarms && (
            <Column
              style={{
                maxHeight: 180,
                overflow: "auto",
                border: "1px solid #e2e8f0",
                borderRadius: 6,
                padding: "4px 12px",
              }}
            >
              {farmStore.farms.length === 0 && (
                <Text size={13} color="#899093" style={{ padding: "8px 0" }}>
                  {t.deviceNoFarms ?? "No farms yet"}
                </Text>
              )}
              {farmStore.farms.map((f) => (
                <Row
                  key={f.id}
                  alignItems="center"
                  justifyContent="space-between"
                  style={{ padding: "6px 0" }}
                >
                  <Text size={14} color="#494C5B">
                    {f.name}
                  </Text>
                  <Checkbox
                    checked={farmIds.includes(f.id)}
                    onChange={() => toggleFarm(f.id)}
                  />
                </Row>
              ))}
            </Column>
          )}
          <SizedBox height={24} />
        </>
      )}
      <Row>
        <Button kind="outline" onClick={handleClose}>
          {lang.devices.deviceCancelButton}
        </Button>
        <SizedBox width={16} />
        <Button
          onClick={!vm.loading ? handleSubmit : undefined}
          disabled={vm.loading ?? disabled}
        >
          {!vm.loading ? lang.devices.deviceSaveButton : <Loading />}
        </Button>
      </Row>
    </Dialog>
  );
};
export default observer(CreateDeviceDialog);
